import {PlayerHistory} from "../models/PlayerHistory";
import {daysAgoKey, normalizeDateKey, todayKey} from "../utils/date";

const STORAGE_KEY = "history";

/**
 * Loads the player's history from localStorage, migrating legacy
 * "YYYY/M/D" date keys to ISO "YYYY-MM-DD" on the way out.
 */
export function loadHistory(): PlayerHistory | null {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored == null) {
        return null;
    }
    try {
        const history: PlayerHistory = JSON.parse(stored);
        if (history.latestDate) {
            history.latestDate = normalizeDateKey(history.latestDate);
        }
        return history;
    } catch {
        return null;
    }
}

export interface TodayStats {
    lapsToday: number;
    daysInARow: number;
    scoreBoard?: boolean[];
    playedToday: boolean;
}

/** History as it applies to today: laps/scoreboard reset if the last lap was another day. */
export function loadTodayStats(): TodayStats {
    const history = loadHistory();
    const playedToday = history?.latestDate === todayKey();
    return {
        lapsToday: playedToday ? history?.lapsToday ?? 0 : 0,
        daysInARow: history?.daysInARow ?? 0,
        scoreBoard: playedToday ? history?.scoreBoard : undefined,
        playedToday,
    };
}

/**
 * Records a completed lap: increments today's lap count, extends or resets
 * the day streak, stores the scoreboard, and persists the result.
 */
export function recordLap(scoreBoard: boolean[]): PlayerHistory {
    const today = todayKey();
    const yesterday = daysAgoKey(1);
    const previous = loadHistory();

    const lapsToday = (previous?.latestDate === today ? previous.lapsToday ?? 0 : 0) + 1;

    let daysInARow: number;
    if (previous?.latestDate === yesterday) {
        daysInARow = (previous.daysInARow ?? 0) + 1;
    } else if (previous?.latestDate === today) {
        daysInARow = previous.daysInARow ?? 1;
    } else {
        daysInARow = 1;
    }

    const history: PlayerHistory = {
        latestDate: today,
        lapsToday,
        daysInARow,
        scoreBoard,
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(history));
    return history;
}
